import { useTranslation } from 'react-i18next';
import type { AscUnresolvedEntity, AscUnresolvedKind } from '../../../utils/timetable/timetableVersion';

export type AscImportLabels = {
  title: string;
  summary: {
    cardsImported: string;
    createdLessons: string;
    createdCards: string;
    createdDivisions: string;
    createdGroups: string;
    linkedEntities: string;
    removedCards: string;
    removedLessons: string;
  };
  kindLabel: (kind: AscUnresolvedKind) => string;
  reasonLabel: (reason: AscUnresolvedEntity['reason']) => string;
};

/**
 * Alle Schlüssel ausgeschrieben statt zusammengesetzt (`${prefix}.x`): i18next-parser findet nur
 * literale Schlüssel und `update:translations` würde alles andere aus den JSON-Dateien entfernen.
 */
export function useAscImportLabels(isPreview: boolean): AscImportLabels {
  const { t } = useTranslation();

  const preview = {
    title: t('pinboard.actions.ascImportPreview.title'),
    summary: {
      cardsImported: t('pinboard.actions.ascImportPreview.cardsImported'),
      createdLessons: t('pinboard.actions.ascImportPreview.createdLessons'),
      createdCards: t('pinboard.actions.ascImportPreview.createdCards'),
      createdDivisions: t('pinboard.actions.ascImportPreview.createdDivisions'),
      createdGroups: t('pinboard.actions.ascImportPreview.createdGroups'),
      linkedEntities: t('pinboard.actions.ascImportPreview.linkedEntities'),
      removedCards: t('pinboard.actions.ascImportPreview.removedCards'),
      removedLessons: t('pinboard.actions.ascImportPreview.removedLessons'),
    },
  };

  const result = {
    title: t('pinboard.actions.ascImportReport.title'),
    summary: {
      cardsImported: t('pinboard.actions.ascImportReport.cardsImported'),
      createdLessons: t('pinboard.actions.ascImportReport.createdLessons'),
      createdCards: t('pinboard.actions.ascImportReport.createdCards'),
      createdDivisions: t('pinboard.actions.ascImportReport.createdDivisions'),
      createdGroups: t('pinboard.actions.ascImportReport.createdGroups'),
      linkedEntities: t('pinboard.actions.ascImportReport.linkedEntities'),
      removedCards: t('pinboard.actions.ascImportReport.removedCards'),
      removedLessons: t('pinboard.actions.ascImportReport.removedLessons'),
    },
  };

  const kindLabel = (kind: AscUnresolvedKind): string => {
    switch (kind) {
      case 'SUBJECT':
        return t('pinboard.actions.ascImportKind.SUBJECT');
      case 'TEACHER':
        return t('pinboard.actions.ascImportKind.TEACHER');
      case 'CLASS':
        return t('pinboard.actions.ascImportKind.CLASS');
      case 'DIVISION':
        return t('pinboard.actions.ascImportKind.DIVISION');
      case 'GROUP':
        return t('pinboard.actions.ascImportKind.GROUP');
      case 'ROOM':
        return t('pinboard.actions.ascImportKind.ROOM');
      case 'PERIOD':
        return t('pinboard.actions.ascImportKind.PERIOD');
      case 'LESSON':
        return t('pinboard.actions.ascImportKind.LESSON');
    }
  };

  const reasonLabel = (reason: AscUnresolvedEntity['reason']): string => {
    switch (reason) {
      case 'AMBIGUOUS':
        return t('pinboard.actions.ascImportReason.AMBIGUOUS');
      // Manuell zugeordnet (bzw. übersprungen) — kein offenes Problem, also nicht „nicht gefunden".
      case 'MAPPED':
        return t('pinboard.actions.ascImportReason.MAPPED');
      case 'NOT_FOUND':
        return t('pinboard.actions.ascImportReason.NOT_FOUND');
    }
  };

  return { ...(isPreview ? preview : result), kindLabel, reasonLabel };
}
